import React from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import {
  formatListeningDateTime,
  formatListeningDeviceId,
  formatListeningNumberWithUnit,
  getListeningCurrentColor,
  getListeningCurrentLabel,
  sortListeningFrequencies,
} from "./listeningViewUtils";

const getComponentFrequencies = (entry) =>
  sortListeningFrequencies(Object.keys(entry?.maxIntensitiesByFrequency || {}));

function ComponentEntryTable({ entry }) {
  const frequencies = getComponentFrequencies(entry);

  if (!frequencies.length) {
    return (
      <Typography variant="body2" color="textSecondary">
        Brak danych częstotliwości.
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell style={{ whiteSpace: "nowrap" }}>Data</TableCell>

            {frequencies.map((frequency) => (
              <TableCell
                key={`head-${entry?.calibrationId}-${frequency}`}
                align="center"
              >
                {frequency} Hz
              </TableCell>
            ))}

            <TableCell align="right" style={{ whiteSpace: "nowrap" }}>
              Kalibrator
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          <TableRow>
            <TableCell style={{ whiteSpace: "nowrap" }}>
              {formatListeningDateTime(entry?.date)}
            </TableCell>

            {frequencies.map((frequency) => (
              <TableCell
                key={`value-${entry?.calibrationId}-${frequency}`}
                align="center"
              >
                {formatListeningNumberWithUnit(
                  entry?.maxIntensitiesByFrequency?.[frequency] ??
                    entry?.maxIntensitiesByFrequency?.[String(frequency)],
                  "dB",
                  1,
                )}
              </TableCell>
            ))}

            <TableCell align="right" style={{ whiteSpace: "nowrap" }}>
              {entry?.calibratorName || "-"}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ComponentEntryCard({ entry }) {
  return (
    <Paper variant="outlined" style={{ padding: 12, marginBottom: 12 }}>
      <ComponentEntryTable entry={entry} />

      <Box mt={1}>
        <Typography variant="body2" color="textSecondary">
          Device ID: {formatListeningDeviceId(entry?.deviceId)}
        </Typography>
      </Box>
    </Paper>
  );
}

function ComponentsSection({ title, items }) {
  return (
    <Paper variant="outlined" style={{ padding: 16, marginBottom: 16 }}>
      <Typography variant="subtitle1" gutterBottom>
        {title}
      </Typography>

      {!items?.length ? (
        <Typography color="textSecondary">Brak pozycji.</Typography>
      ) : (
        items.map((entry, index) => (
          <ComponentEntryCard
            key={entry?.calibrationId || `${title}-${index}`}
            entry={entry}
          />
        ))
      )}
    </Paper>
  );
}

export default function ListeningComponentsView({ data, loading, error }) {
  if (loading) {
    return (
      <Box py={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  if (!data) {
    return (
      <Typography color="textSecondary">
        Brak danych o składowych odsłuchu.
      </Typography>
    );
  }

  const statusLabel = getListeningCurrentLabel(data.current);
  const statusColor = getListeningCurrentColor(data.current);

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        {statusLabel !== "-" ? (
          <Chip
            label={statusLabel}
            style={{
              backgroundColor: statusColor,
              color: "#fff",
            }}
          />
        ) : null}
      </Box>

      <ComponentsSection title="WSPÓLNE" items={data.common || []} />

      <ComponentsSection
        title="NIEPODPIĘTE UŻYTE PODCZAS ODSŁUCHU"
        items={data.usedDuringListeningButNoLongerAttached || []}
      />

      <ComponentsSection
        title="NIEUŻYTE PODCZAS ODSŁUCHU"
        items={data.currentlyAttachedButNotUsedDuringListening || []}
      />
    </Box>
  );
}
